/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {Alert, Breadcrumb, Card, Descriptions, Modal, notification, Result, Space, Tabs, Tooltip} from "antd";
import {Link, useParams} from "react-router-dom";
import {Customer, Device, Todo} from "../core/datamodel";
import {del, get, patch} from "../core/request";
import {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../store";
import {ViewState} from "../states";
import {openDrawer, openUpload} from "../reducers/drawers-reducer";
import {CustomButton} from "../components/buttons";
import {IconSet} from "../components/icons";
import Loading from "../components/loading";
import {ApiTable} from "../components/table";
import NoteCard from "../components/noteCard";

export default function CustomerDetailsView(props: any) {
    const params: { id: string } = useParams();
    const reloadSignal = useAppSelector(state => state.reloadSignals.customer)
    const [customer, setCustomer] = useState({} as Customer);
    const [state, setState] = useState(ViewState.loading);
    const [permissions, setPermissions] = useState({} as any);

    useEffect(() => {
        get("customers/" + params.id).done((response) => {
                setCustomer(new Customer(response.data.customer));
                setState(ViewState.success);
            }
        ).fail(() => setState(ViewState.failed))
    }, [params.id, reloadSignal])

    useEffect(() => {
        get("customers/" + params.id + "/permissions").done((response) => {
                setPermissions(response.data.permissions);
            }
        )
    }, [params.id, reloadSignal])

    let dispatch = useAppDispatch();

    const onEdit = () => dispatch(openDrawer({key: 'customer', object: customer}));

    const onSave = (data: string) => {
        patch("customers/" + params.id, {"comment": data}).done((response) => {
            setCustomer(new Customer(response.data.device))
        }).fail(() => notification.error({
            message: "Fehler",
            description: "Notiz konnte nicht gespeichert werden.",
            duration: 30,
        }))
    }

    const breadcrumb = (
        <Breadcrumb style={{paddingTop: 6, paddingBottom: 6}}>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/customers">Kunden</Link></Breadcrumb.Item>
            <Breadcrumb.Item>{!!customer.props?.repr ? customer.props.repr : "Unknown"}</Breadcrumb.Item>
        </Breadcrumb>
    )

    const pageHeader = (
        <Card
            title={
                (!!customer.props?.repr ? customer.props.repr : "Unknown") +
                (!!customer?.props?.alias && !!customer?.props?.name ? " ( " + customer.props.name + " )" : "")}
            extra={
                <Space>
                    <CustomButton
                        mode={'edit'}
                        onClick={onEdit}
                        tooltip={"Kunde bearbeiten"}
                        disabled={!(
                            !!permissions?.customer?.delete
                            || !!permissions?.customer?.put
                            || !!permissions?.customer?.patch
                        )}
                    />
                </Space>
            }>
            <Descriptions colon={false} size="small" column={2}>

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"CEO"}>
                            <IconSet type='contact_ceo' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={customer?.props?.contact_ceo ?
                        <Tooltip overlay={"CEO"}>
                            <Link to={"/persons/" + customer.props.contact_ceo.props.uuid}>
                                {customer.props.contact_ceo.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Rechnungskontakt"}>
                            <IconSet type='contact_invoice' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={customer?.props?.contact_invoice ?
                        <Tooltip overlay={"Rechnungskontakt"}>
                            <Link to={"/persons/" + customer.props.contact_invoice.props.uuid}>
                                {customer.props.contact_invoice.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={<Tooltip overlay={"Ansprechpartner IT"}>
                        <IconSet type='contact_it' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                    </Tooltip>}
                    children={customer?.props?.contact_it ?
                        <Tooltip overlay={"Ansprechpartner IT"}>
                            <Link to={"/persons/" + customer.props.contact_it.props.uuid}>
                                {customer.props.contact_it.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Strahlenschutzverantwortlicher"}>
                            <IconSet type='contact_radiation' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={customer?.props?.contact_radiation_protection_officer ?
                        <Tooltip overlay={"Strahlenschutzverantwortlicher"}>
                            <Link to={"/persons/" + customer.props.contact_radiation_protection_officer.props.uuid}>
                                {customer.props.contact_radiation_protection_officer.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Rechnungsadresse"}>
                            <IconSet type='address' style={{lineHeight: '24px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Rechnungsadresse"}>
                        {customer?.props?.street} {customer?.props?.house_nr} <br/>
                        {customer?.props?.additional_adresse ? (<>{customer?.props?.additional_adresse}<br/></>) : (<></>)}
                        {customer?.props?.postcode} {customer?.props?.city}
                    </Tooltip>}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Verantwortlicher MPE"}>
                            <IconSet type='mpe' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={customer?.props?.mpe ?
                        <Tooltip overlay={"Verantwortlicher MPE"}>
                            <Link to={"/persons/" + customer.props.mpe.props.uuid}>
                                {customer.props.mpe.props.repr}
                            </Link>
                        </Tooltip> : ""}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Synology-Link"}>
                            <IconSet type='link' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Synology-Link"}>
                        {customer?.props?.synologylink ?
                            <a href={customer?.props?.synologylink}
                               target="_blank">{customer?.props?.synologylink}</a> : ""
                        }
                    </Tooltip>}
                />

            </Descriptions>
        </Card>
    )

    const disabledAlert = (
        !!customer?.props?.disabled ? (<Alert
            message="Dieser Kunde ist deaktiviert!"
            description="Zur weiteren Verwendnung bearbeiten und reaktivieren."
            type="warning"
            showIcon={true}
            style={{marginBottom: 24}}
        />) : (<></>)
    )

    const tabs = (
        <Tabs
            defaultActiveKey='2'  // Always start on devices {customer?.props?.comment ? "1" : "2"}
            style={{minHeight: '100%'}}
            animated
            tabBarStyle={{paddingLeft: '12px', marginBottom: 12}}>
            <Tabs.TabPane
                tab={<><IconSet type='notes'/> Notizen</>}
                key="1"
                disabled={!customer?.props?.comment}
            >
                <NoteCard text={customer?.props?.comment ? customer?.props?.comment : ""} onSave={onSave}/>
            </Tabs.TabPane>

            <Tabs.TabPane
                tab={<><IconSet type='devices'/> Geräte</>}
                key="2"
            >
                <ApiTable
                    title={<>Geräte</>}
                    storeFilters={true}
                    id="Kunden_Geräte"
                    url={"customers/" + customer?.props?.uuid + "/devices"}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.customer),
                        useAppSelector(state => state.reloadSignals.device),
                        useAppSelector(state => state.reloadSignals.upload),
                    ]}
                    clickable={"/devices"}
                    buttons={
                        <CustomButton
                            mode={'add'}
                            tooltip={"Neues Gerät hinzufügen"}
                            disabled={!permissions?.device?.post}
                            onClick={() => dispatch(openDrawer({
                                key: 'device',
                                object: new Device({'customer': {'uuid': customer?.props?.uuid}})
                            }))}
                        />
                    }
                    actionWidth={69}
                    actions={(text: any, record: any) => (
                        <Space size={12}>
                            <Tooltip
                                overlay='Bericht erstellen'>
                                <Link
                                    style={{color: '#1890ff'}}
                                    onClick={
                                        (e) => {
                                            e.stopPropagation();
                                        }
                                    }
                                    to={"/devices/" + record.key + "/report"}
                                >
                                    <IconSet type='report'/>
                                </Link>
                            </Tooltip>
                            <Tooltip
                                overlay='Daten hochladen'>
                                <div
                                    onClick={
                                        (e) => {
                                            dispatch(openUpload(
                                                record.key
                                            ));
                                            e.stopPropagation();
                                        }
                                    }
                                    style={{color: '#1890ff'}}
                                >
                                    <IconSet type='upload'/>
                                </div>
                            </Tooltip>
                        </Space>
                    )
                    }
                />
            </Tabs.TabPane>

            <Tabs.TabPane
                tab={<><IconSet type='persons'/> Kontakte</>}
                key="3"
            >
                <ApiTable
                    title={<>Zugehörige Kontakte</>}
                    id="Kunden_Kontakte"
                    url={"customers/" + customer?.props?.uuid + "/employees"}
                    storeFilters={true}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.person),
                        useAppSelector(state => state.reloadSignals.customer),
                        useAppSelector(state => state.reloadSignals.device)
                    ]}
                    clickable={"/persons"}
                />
            </Tabs.TabPane>

            <Tabs.TabPane
                tab={<><IconSet type='note'/> Todos</>}
                key="4"
            >
                <ApiTable
                    title={<>Todos des Kunden</>}
                    id="Kunden_Todos"
                    url={"todos"}
                    url_params={"customer=" + customer?.props?.uuid}
                    storeFilters={true}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.todo),
                    ]}
                    actions={(text: any, record: any) =>
                        <div style={{display: "inline-flex"}}>
                            <CustomButton
                                mode={'delete'}
                                noBorder
                                onClick={() => Modal.confirm({
                                    title: "Aufgabe überspringen",
                                    content: "Sollen diese Aufgabe wirklich übersprungen werden? Das bearbeiten der Aufgabe löst diesen Eintrag automatisch.",
                                    maskClosable: true,
                                    cancelText: 'Abbrechen',
                                    okText: 'Überspringen',
                                    okType: 'danger',
                                    autoFocusButton: 'cancel',
                                    onOk: () => {
                                        del("todos/" + record.key).catch((error) => {
                                            notification.error({
                                                message: "Fehlende Berechtigungen",
                                                description: "Todo konnte nicht als erledigt markiert werden.",
                                                duration: 30,
                                            })
                                        })
                                    }
                                })
                                }
                                tooltip={"Aufgabe überspringen"}
                            />

                            {record.is_editable ? (
                                <CustomButton
                                    mode={'edit'}
                                    noBorder
                                    onClick={() => {
                                        get("todos/" + record.key).done((response) => {
                                            dispatch(openDrawer({key: 'todo', object: new Todo(response.data.todo)}))
                                        })

                                    }}
                                    tooltip={"Aufgabe bearbeiten"}
                                />
                            ) : (
                                <div style={{width: "2rem", height: "1rem"}} />
                            )
                            }

                        </div>
                    }
                    buttons={
                        <CustomButton
                            mode={'add'}
                            tooltip={"Neues Todo hinzufügen"}
                            onClick={() => dispatch(openDrawer({key: 'todo', object: new Todo({customer: customer.dump()})}))}
                        />
                    }
                />
            </Tabs.TabPane>
        </Tabs>
    )

    switch (state) {
        case ViewState.loading:
            return (
                <>
                    {breadcrumb}
                    <Loading/>
                </>
            )
        case ViewState.success:
            return (
                <>
                    {breadcrumb}
                    {disabledAlert}
                    {pageHeader}
                    {tabs}
                </>
            )

        case ViewState.failed:
        default:
            return (
                <>
                    {breadcrumb}
                    <Result
                        status="404"
                        title="Nicht gefunden"
                        subTitle="Entschuldigung, der gesuchte Kunde konnte nicht gefunden werden!"
                    />
                </>
            )
    }
}
